import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { parse, format } from "date-fns";
import {
    MapPin, Star, Home, PawPrint, Wifi, Bed, Bath, Car, ArrowLeft, Heart,
    Phone, Mail, User, ChevronDown, ChevronUp, Grid3x3, Image as ImageIcon,
    Send, Share2
} from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import LazyImage from "../components/LazyImage";
import { API_BASE_URL } from "../config";

const AccommodationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(0);
    const [viewMode, setViewMode] = useState("photos"); // photos, grid, floorplan, video, map
    const [showAllPhotos, setShowAllPhotos] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        overview: true,
        rooms: false,
        features: false,
        building: false,
        other: false
    });
    const [relatedProperties, setRelatedProperties] = useState([]);

    // Contact form state
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        mobile: '',
        message: "I'm interested in this property, please contact me."
    });

    useEffect(() => {
        const fetchListing = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/api/public/listings/${id}`);
                if (!response.ok) {
                    throw new Error('Listing not found');
                }
                const data = await response.json();
                setListing(data);

                if (data.parent_building_id) {
                    fetchRelatedProperties(data.parent_building_id, data.id);
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching listing:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchListing();
        }
    }, [id]);

    const fetchRelatedProperties = async (parentBuildingId, currentListingId) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/public/listings?parent_building_id=${parentBuildingId}&per_page=8`
            );
            if (response.ok) {
                const data = await response.json();
                const filtered = data.data?.filter(prop => prop.id !== currentListingId) || [];
                setRelatedProperties(filtered.slice(0, 4));
            }
        } catch (err) {
            console.error('Error fetching related properties:', err);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Helper functions
    const getBedroomCount = () => {
        if (!listing?.bedroom_beds || listing.bedroom_beds.length === 0) return 0;
        const uniqueBedrooms = new Set(
            listing.bedroom_beds.map(bed => bed.floor_plan_bedroom_id)
        );
        return uniqueBedrooms.size;
    };

    const getBathroomCount = () => {
        return listing?.building?.floor_plans?.[0]?.bathrooms || 1;
    };

    const getParkingCount = () => {
        const floorPlan = listing?.building?.floor_plans?.[0];
        return (floorPlan?.garages || 0) + (floorPlan?.parking_bays || 0);
    };

    const getAreaSqm = () => {
        if (!listing?.building?.area_sqm) return null;
        return Math.round(parseFloat(listing.building.area_sqm));
    };

    const getPropertyName = () => {
        return listing?.building?.name || "Property";
    };

    const getPropertyAddress = () => {
        return listing?.building?.street_address || "";
    };

    const getImages = () => {
        if (listing?.images && listing.images.length > 0) {
            return listing.images.map(img => img.signed_url);
        }
        return ["/images/placeholder image.png"];
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        try {
            const parsedTime = parse(timeString, 'HH:mm:ss', new Date());
            return format(parsedTime, 'HH:mm');
        } catch (error) {
            console.error('Error formatting time:', error);
            return timeString;
        }
    };

    const handleContactSubmit = (e) => {
        e.preventDefault();
        console.log('Contact form submitted:', contactForm);
        // Add your form submission logic here
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-[#F2F1F0] text-[#144E73] font-sans min-h-screen pt-16 pb-20 px-6">
                <div className="max-w-7xl mx-auto text-center py-12">
                    <p className="text-lg">Loading property details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !listing) {
        return (
            <div className="bg-[#F2F1F0] text-[#144E73] font-sans min-h-screen pt-16 pb-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center py-12 bg-white rounded-xl shadow-md">
                        <p className="text-red-600 mb-4">Property not found</p>
                        <button
                            onClick={() => navigate('/accommodation')}
                            className="px-6 py-2 bg-[#F29E38] text-white rounded-full hover:bg-opacity-90 transition"
                        >
                            Back to Accommodation
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const images = getImages();
    const bedroomCount = getBedroomCount();
    const bathroomCount = getBathroomCount();
    const parkingCount = getParkingCount();
    const areaSqm = getAreaSqm();
    const displayPhotos = showAllPhotos ? images : images.slice(0, 9);

    return (
        <div className="bg-[#F2F1F0] text-[#144E73] font-sans min-h-screen pt-16 pb-20">
            {/* Back Button */}
            <div className="max-w-7xl mx-auto px-6 mb-2">
                <button
                    onClick={() => navigate('/accommodation')}
                    className="flex items-center gap-2 text-primary hover:text-[#F29E38] transition text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Accommodation</span>
                </button>
            </div>

            {/* Image Viewer */}
            <div className="max-w-7xl mx-auto px-6">
                <div className="relative">
                    <div className="aspect-video w-full overflow-hidden bg-black rounded-lg">
                        <LazyImage
                            src={images[selectedImage]}
                            alt={getPropertyName()}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Image Counter */}
                    <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded text-sm">
                        {selectedImage + 1} of {images.length}
                    </div>

                    {/* View Mode Toggle Bar */}
                    <div className="absolute bottom-0 left-0 right-0 bg-[#144E73] bg-opacity-95 text-white rounded-b-lg">
                        <div className="px-6 py-3 flex items-center gap-6">
                        <button
                            onClick={() => setLightboxOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded transition hover:bg-[#F29E38] hover:bg-opacity-50"
                        >
                            <Grid3x3 className="w-4 h-4" />
                            <span className="text-sm">Photo Grid</span>
                        </button>
                        <button
                            onClick={() => setViewMode("photos")}
                            className={`flex items-center gap-2 px-4 py-2 rounded transition ${
                                viewMode === "photos" ? "bg-[#F29E38]" : "hover:bg-[#F29E38] hover:bg-opacity-50"
                            }`}
                        >
                            <ImageIcon className="w-4 h-4" />
                            <span className="text-sm">Photos</span>
                        </button>
                        {listing.building?.floor_plans && listing.building.floor_plans.length > 0 && (
                            <button
                                onClick={() => setViewMode("floorplan")}
                                className={`flex items-center gap-2 px-4 py-2 rounded transition ${
                                    viewMode === "floorplan" ? "bg-[#F29E38]" : "hover:bg-[#F29E38] hover:bg-opacity-50"
                                }`}
                            >
                                <Home className="w-4 h-4" />
                                <span className="text-sm">Floor Plan</span>
                            </button>
                        )}
                    </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 mt-8">
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Left Column - Property Details */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Price & Title Section */}
                        <div className="bg-white rounded-xl shadow-md p-5">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h1 className="text-2xl font-bold text-[#144E73] mb-2">
                                        R555 000(will be dynamic price)
                                    </h1>
                                    <h2 className="text-lg text-gray-700 mb-2">
                                        {getPropertyName()}
                                    </h2>
                                    <div className="flex items-center gap-2 text-[#F29E38] hover:underline cursor-pointer text-sm">
                                        <MapPin className="w-4 h-4" />
                                        <span>{getPropertyAddress()}</span>
                                    </div>
                                </div>
                                <button className="p-2 hover:bg-gray-100 rounded-full transition">
                                    <Heart className="w-5 h-5 text-gray-400" />
                                </button>
                            </div>

                            {/* Property Stats Row */}
                            <div className="flex items-center gap-6 pt-3 border-t border-gray-200">
                                <div className="flex items-center gap-2 text-gray-700 text-sm">
                                    <Bed className="w-4 h-4" />
                                    <span className="font-semibold">{bedroomCount}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-700 text-sm">
                                    <Bath className="w-4 h-4" />
                                    <span className="font-semibold">{bathroomCount}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-700 text-sm">
                                    <Car className="w-4 h-4" />
                                    <span className="font-semibold">{parkingCount}</span>
                                </div>
                                {areaSqm && (
                                    <div className="flex items-center gap-2 text-gray-700 text-sm">
                                        <Home className="w-4 h-4" />
                                        <span className="font-semibold">{areaSqm} m²</span>
                                    </div>
                                )}
                            </div>

                            {/* Badges */}
                            {listing.furnished && (
                                <div className="mt-4">
                                    <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-semibold">
                                        FURNISHED
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div className="bg-white rounded-xl shadow-md p-5">
                            <h3 className="text-base font-semibold text-[#144E73] mb-3">About This Property</h3>
                            {listing.description ? (
                                <p className="text-gray-700 leading-relaxed whitespace-pre-line text-sm">
                                    {listing.description}
                                </p>
                            ) : (
                                <p className="text-gray-500 italic text-sm">No description available.</p>
                            )}

                            {/* Check-in Info */}
                            {listing.check_in && (
                                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                    <h4 className="font-semibold text-[#144E73] mb-2 text-sm">Check-in Information</h4>
                                    <p className="text-sm text-gray-700 mb-3">{listing.check_in.instructions}</p>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Check-in:</span>
                                            <span className="ml-2 font-medium">{formatTime(listing.check_in.check_in_from)} - {formatTime(listing.check_in.check_in_to)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Check-out:</span>
                                            <span className="ml-2 font-medium">By {formatTime(listing.check_in.check_out_by)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Features Grid */}
                        {listing.property_features && listing.property_features.length > 0 && (
                            <div className="bg-white rounded-xl shadow-md p-5">
                                <h3 className="text-base font-semibold text-[#144E73] mb-3">Features</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <Bed className="w-4 h-4 text-[#F29E38]" />
                                        <span className="text-sm">Bedrooms: {bedroomCount}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <Bath className="w-4 h-4 text-[#F29E38]" />
                                        <span className="text-sm">Bathrooms: {bathroomCount}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-700">
                                        <Car className="w-4 h-4 text-[#F29E38]" />
                                        <span className="text-sm">Parking: {parkingCount}</span>
                                    </div>
                                    {listing.pet_friendly && (
                                        <div className="flex items-center gap-2 text-gray-700">
                                            <PawPrint className="w-4 h-4 text-[#F29E38]" />
                                            <span className="text-sm">Pet Friendly</span>
                                        </div>
                                    )}
                                    {listing.internet && (
                                        <div className="flex items-center gap-2 text-gray-700">
                                            <Wifi className="w-4 h-4 text-[#F29E38]" />
                                            <span className="text-sm">WiFi</span>
                                        </div>
                                    )}
                                    {listing.property_features.map((feature, index) => (
                                        <div key={index} className="flex items-center gap-2 text-gray-700">
                                            <span className="w-2 h-2 bg-[#F29E38] rounded-full"></span>
                                            <span className="text-sm">{feature.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Photo Gallery */}
                        {images.length > 1 && (
                            <div className="bg-white rounded-xl shadow-md p-5">
                                <h3 className="text-base font-semibold text-[#144E73] mb-3">Photo Gallery</h3>
                                <div className="grid grid-cols-3 gap-3">
                                    {displayPhotos.map((src, index) => (
                                        <div
                                            key={index}
                                            onClick={() => {
                                                setSelectedImage(index);
                                                setLightboxOpen(true);
                                            }}
                                            className="aspect-[4/3] rounded-lg overflow-hidden cursor-pointer border-2 border-gray-200 hover:border-[#F29E38] transition"
                                        >
                                            <LazyImage
                                                src={src}
                                                alt={`${getPropertyName()} - Image ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                                {images.length > 9 && !showAllPhotos && (
                                    <button
                                        onClick={() => setShowAllPhotos(true)}
                                        className="w-full mt-4 py-2 border-2 border-[#F29E38] text-[#F29E38] rounded-lg hover:bg-[#F29E38] hover:text-white transition font-semibold"
                                    >
                                        View More
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Property Overview Section */}
                        <div className="bg-white rounded-xl shadow-md overflow-hidden">
                            {/* Overview Toggle */}
                            <button
                                onClick={() => toggleSection('overview')}
                                className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
                            >
                                <h3 className="text-base font-semibold text-[#144E73]">Property Overview</h3>
                                {expandedSections.overview ? (
                                    <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                            </button>

                            {expandedSections.overview && (
                                <div className="px-5 pb-5">
                                    <table className="w-full text-sm">
                                        <tbody className="divide-y divide-gray-200">
                                            {listing.building?.unit_nr && (
                                                <tr>
                                                    <td className="py-2 text-gray-600">Unit Number</td>
                                                    <td className="py-2 font-medium text-gray-800">{listing.building.unit_nr}</td>
                                                </tr>
                                            )}
                                            {listing.building?.building_type && (
                                                <tr>
                                                    <td className="py-2 text-gray-600">Type of Property</td>
                                                    <td className="py-2 font-medium text-gray-800">{listing.building.building_type.name}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td className="py-2 text-gray-600">Street Address</td>
                                                <td className="py-2 font-medium text-[#F29E38]">{getPropertyAddress()}</td>
                                            </tr>
                                            {listing.layout && (
                                                <tr>
                                                    <td className="py-2 text-gray-600">Layout</td>
                                                    <td className="py-2 font-medium text-gray-800 capitalize">{listing.layout.replace('_', ' ')}</td>
                                                </tr>
                                            )}
                                            {areaSqm && (
                                                <tr>
                                                    <td className="py-2 text-gray-600">Floor Size</td>
                                                    <td className="py-2 font-medium text-gray-800">{areaSqm} m²</td>
                                                </tr>
                                            )}
                                            {listing.pet_friendly !== null && (
                                                <tr>
                                                    <td className="py-2 text-gray-600">Pets Allowed</td>
                                                    <td className="py-2 font-medium text-gray-800">{listing.pet_friendly ? 'Yes' : 'No'}</td>
                                                </tr>
                                            )}
                                            {listing.furnished !== null && (
                                                <tr>
                                                    <td className="py-2 text-gray-600">Furnished</td>
                                                    <td className="py-2 font-medium text-gray-800">{listing.furnished ? 'Yes' : 'No'}</td>
                                                </tr>
                                            )}
                                            {listing.building?.municipality && (
                                                <tr>
                                                    <td className="py-2 text-gray-600">Municipality</td>
                                                    <td className="py-2 font-medium text-gray-800">{listing.building.municipality.name}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Bedroom Configuration */}
                        {listing.bedroom_beds && listing.bedroom_beds.length > 0 && (
                            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                                <button
                                    onClick={() => toggleSection('rooms')}
                                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
                                >
                                    <h3 className="text-base font-semibold text-[#144E73]">Rooms</h3>
                                    {expandedSections.rooms ? (
                                        <ChevronUp className="w-5 h-5 text-gray-600" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-600" />
                                    )}
                                </button>

                                {expandedSections.rooms && (
                                    <div className="px-5 pb-5">
                                        <div className="space-y-2">
                                            {listing.bedroom_beds.map((bed, index) => (
                                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                                                    <span className="font-medium text-gray-800">
                                                        Bedroom {bed.floor_plan_bedroom?.bedroom_number}
                                                    </span>
                                                    <span className="text-gray-600">
                                                        {bed.quantity}x {bed.bed_type}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Directions */}
                        {listing.route_direction && (
                            <div className="bg-white rounded-xl shadow-md p-5">
                                <h3 className="text-base font-semibold text-[#144E73] mb-3">{listing.route_direction.route_name}</h3>
                                <p className="text-gray-700 whitespace-pre-line text-sm">{listing.route_direction.description}</p>
                            </div>
                        )}

                        {/* House Rules */}
                        {listing.house_rule && (
                            <div className="bg-white rounded-xl shadow-md p-5">
                                <h3 className="text-base font-semibold text-[#144E73] mb-3">{listing.house_rule.name}</h3>
                                <p className="text-gray-700 whitespace-pre-line text-sm">{listing.house_rule.rules}</p>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Contact Agent */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 space-y-4">
                            {/* Contact Agent Card */}
                            <div className="bg-white rounded-xl shadow-md p-5">
                                <h3 className="text-lg font-semibold text-[#144E73] mb-4 text-center">Contact Agent</h3>

                                {/* Agent Info */}
                                {listing.building?.agent && (
                                    <div className="mb-4 text-center">
                                        <p className="font-bold text-gray-800 text-base">{listing.building.agent.name_body_corporate}</p>
                                        {listing.building.agent.abbreviation && (
                                            <p className="text-xs text-gray-600">({listing.building.agent.abbreviation})</p>
                                        )}
                                    </div>
                                )}

                                {/* Quick Contact Buttons */}
                                {listing.building?.agent && (
                                    <div className="space-y-2 mb-4">
                                        {listing.building.agent.office_contact_number && (
                                            <a
                                                href={`tel:${listing.building.agent.office_contact_number}`}
                                                className="w-full flex items-center justify-center gap-2 py-2 border-2 border-[#F29E38] text-[#F29E38] rounded-lg hover:bg-[#F29E38] hover:text-white transition font-semibold text-sm"
                                            >
                                                <Phone className="w-4 h-4" />
                                                Show Contact Number
                                            </a>
                                        )}
                                        {listing.building.agent.office_contact_number && (
                                            <a
                                                href={`https://wa.me/${listing.building.agent.office_contact_number.replace(/[^0-9]/g, '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-full flex items-center justify-center gap-2 py-2 border-2 border-[#144E73] text-[#144E73] rounded-lg hover:bg-[#144E73] hover:text-white transition font-semibold text-sm"
                                            >
                                                <Phone className="w-4 h-4" />
                                                WhatsApp Agent
                                            </a>
                                        )}
                                    </div>
                                )}

                                {/* Contact Form */}
                                <form onSubmit={handleContactSubmit} className="space-y-3">
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Your Name"
                                            value={contactForm.name}
                                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F29E38] focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="email"
                                            placeholder="Your Email"
                                            value={contactForm.email}
                                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F29E38] focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="tel"
                                            placeholder="Your Mobile Number"
                                            value={contactForm.mobile}
                                            onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F29E38] focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <textarea
                                        placeholder="Message"
                                        value={contactForm.message}
                                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F29E38] focus:border-transparent resize-none"
                                    ></textarea>

                                    <button
                                        type="submit"
                                        className="w-full bg-[#144E73] text-white py-2 rounded-lg font-semibold hover:bg-opacity-90 transition flex items-center justify-center gap-2 text-sm"
                                    >
                                        <Send className="w-4 h-4" />
                                        Send Message
                                    </button>

                                    <p className="text-xs text-center text-gray-500">
                                        By continuing I understand and agree with the Terms & Conditions and Privacy Policy.
                                    </p>
                                </form>
                            </div>

                            {/* Company/Owner Info */}
                            {listing.building?.company && (
                                <div className="bg-white rounded-xl shadow-md p-5 text-center">
                                    <p className="font-bold text-gray-800 text-base">{listing.building.company.company}</p>
                                    {listing.building.company.address && (
                                        <p className="text-xs text-gray-600 mt-2">{listing.building.company.address}</p>
                                    )}
                                </div>
                            )}

                            {/* Share */}
                            <div className="bg-white rounded-xl shadow-md p-5">
                                <h4 className="text-base font-semibold text-[#144E73] mb-3 text-center">Share</h4>
                                <div className="flex items-center justify-center gap-3">
                                    <button className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                                        <Share2 className="w-4 h-4" />
                                    </button>
                                    <button className="p-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition">
                                        <Mail className="w-4 h-4" />
                                    </button>
                                    <button className="p-2 bg-[#144E73] text-white rounded-lg hover:bg-opacity-90 transition">
                                        <Phone className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Properties */}
                {relatedProperties.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-xl font-bold text-[#144E73] mb-4">Related Properties</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {relatedProperties.map((property) => {
                                const propBedroomCount = property.bedroom_beds ? new Set(property.bedroom_beds.map(b => b.floor_plan_bedroom_id)).size : 0;
                                const propBathroomCount = property.building?.floor_plans?.[0]?.bathrooms || 1;
                                const propAreaSqm = property.building?.area_sqm ? Math.round(parseFloat(property.building.area_sqm)) : null;
                                const propImage = property.images?.[0]?.signed_url || "/images/placeholder image.png";
                                const hasParking_ = property.property_features?.some(feature =>
                                    feature.name.toLowerCase().includes('parking')
                                ) || false;

                                return (
                                    <div
                                        key={property.id}
                                        onClick={() => navigate(`/accommodation/${property.id}`)}
                                        className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer"
                                    >
                                        <div className="relative aspect-[4/3] w-full overflow-hidden">
                                            <LazyImage
                                                src={propImage}
                                                alt={property.building?.name || "Property"}
                                                className="w-full h-full object-cover"
                                            />
                                            {property.star_grading && (
                                                <div className="absolute top-3 right-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                                    <Star className="w-3 h-3 fill-white" />
                                                    {property.star_grading}
                                                </div>
                                            )}
                                            {property.pet_friendly && (
                                                <div className="absolute top-3 left-3 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                                    <PawPrint className="w-3 h-3" />
                                                    Pet Friendly
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-4">
                                            <div className="flex items-center justify-between text-sm text-gray-600 mb-3 pb-3 border-b border-gray-200">
                                                {propAreaSqm && (
                                                    <div className="flex flex-col items-center">
                                                        <span className="font-bold text-gray-800">{propAreaSqm}</span>
                                                        <span className="text-xs">m²</span>
                                                    </div>
                                                )}
                                                <div className="flex flex-col items-center">
                                                    <span className="font-bold text-gray-800">{propBathroomCount}</span>
                                                    <span className="text-xs">BATHS</span>
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <span className="font-bold text-gray-800">{propBedroomCount}</span>
                                                    <span className="text-xs">BEDS</span>
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <span className="font-bold text-gray-800">{hasParking_ ? '1' : '0'}</span>
                                                    <span className="text-xs">PARKING</span>
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className="text-base font-bold text-[#144E73] mb-1">
                                                    {property.building?.name || "Property"}
                                                </h3>
                                                <div className="flex items-start gap-1 text-sm text-gray-600">
                                                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                    <p>{property.building?.street_address || ""}</p>
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {property.furnished && (
                                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                            Furnished
                                                        </span>
                                                    )}
                                                    {property.internet && (
                                                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                                            WiFi
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Lightbox */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={images.map(src => ({ src }))}
                index={selectedImage}
                on={{
                    view: ({ index }) => setSelectedImage(index)
                }}
                carousel={{ padding: 0, spacing: 0 }}
                controller={{ closeOnBackdropClick: true }}
                styles={{
                    container: { backgroundColor: "rgba(0, 0, 0, 0.95)" },
                    slide: { padding: 0 }
                }}
            />
        </div>
    );
};

export default AccommodationDetail;
